/**
 * NavbarVisitorPill.js  —  v34.92 (Feb 13 2026)
 *
 * Compact "You're visitor #N" pill for the public navbar (sits before
 * the "Sign In" button). Shares the same `/api/visits/{track,count}`
 * backend as the trust-bar <VisitorCounter />, so they grow in lockstep.
 *
 * Two display modes (auto-switches based on count):
 *  - count < 100 → "👋 You're visitor #N"  (early-access feel, FOMO)
 *  - count ≥ 100 → "🌍 N visitors and growing"  (social proof at scale)
 *
 * Privacy: backend stores ONLY a SHA-256 hash of (IP + UA + salt), with
 * per-day dedup so refreshes don't inflate the number. No cookies, no
 * fingerprinting, no third-party trackers.
 */
import React, { useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => n.toLocaleString('en-US');

// Detect obvious bots client-side so we don't even POST. Belt-and-suspenders
// — backend also filters on UA. The `webdriver` flag catches automation.
const isBot = () => {
  if (typeof navigator === 'undefined') return false;
  if (navigator.webdriver) return true;
  const ua = (navigator.userAgent || '').toLowerCase();
  return /bot|crawler|spider|headless|phantomjs|puppeteer|playwright|selenium/i.test(ua);
};

const NavbarVisitorPill = () => {
  const [count, setCount] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      // Track this visit (fire-and-forget — never block UI). The trust-bar
      // <VisitorCounter /> also POSTs /track on mount, but the backend
      // dedupes per (visitor_hash, day) so the second POST is a no-op.
      if (!isBot()) {
        try {
          await fetch(`${API}/api/visits/track`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            keepalive: true,
          });
        } catch (_) { /* offline / blocked — fine */ }
      }
      // Read the live count
      try {
        const res = await fetch(`${API}/api/visits/count`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && typeof data.total === 'number') {
          setCount(data.total);
        }
      } catch (_) { /* silent */ }
    }

    init();
    return () => { cancelled = true; };
  }, []);

  // Hide while loading — avoid layout shift
  if (count === null) return null;

  // Two-mode display
  const isEarly = count < 100;
  const label = isEarly
    ? <>You&apos;re visitor <span className="text-emerald-300 font-bold tabular-nums">#{fmt(count)}</span></>
    : <><span className="text-emerald-300 font-bold tabular-nums">{fmt(count)}</span> visitors and growing</>;

  return (
    <div
      className="hidden lg:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/25 hover:bg-emerald-500/15 hover:border-emerald-500/40 transition-colors cursor-default"
      data-testid="navbar-visitor-pill"
      title="Live, deduped per-day per-visitor count · privacy-safe (no cookies, no fingerprint)"
    >
      <span className="relative flex h-1.5 w-1.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
      </span>
      <span className="text-[11px] text-slate-300 whitespace-nowrap" data-testid="navbar-visitor-pill-label">
        {label}
      </span>
    </div>
  );
};

export default NavbarVisitorPill;
